import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertCircle, CheckCircle2, FileText, Send, Upload } from 'lucide-react';
import { ApiClientError, Spinner, StatusBadge, relativeTime } from '@riskpay/ui-components';
import type { DocumentType, EntityType, MerchantDocument } from '@riskpay/types';
import { api, useAuth } from '../lib/client';

/* ==========================================================================
 *  KYC onboarding wizard
 *
 *  The required-document list is derived from the entity type, and it is the
 *  same list published on riskpay.in/#requirements — a prospect is never asked
 *  for something they were not told about before signing up.
 * ========================================================================== */

interface Requirement {
  type: DocumentType;
  label: string;
  required: boolean;
  hint?: string;
  /**
   * Alternatives that satisfy the same requirement. Bank proof is the case
   * that matters: a cancelled cheque and a three-month statement prove the
   * same thing, and plenty of merchants — especially newer digital-first
   * accounts — simply do not have a chequebook.
   *
   * Uploading any one of the group satisfies it.
   */
  alternatives?: { type: DocumentType; label: string; hint?: string }[];
}

/** The requirement plus whichever of its alternatives was actually uploaded. */
function satisfiedBy(
  requirement: Requirement,
  byType: Map<string, MerchantDocument>,
): { doc?: MerchantDocument; usedType: DocumentType } {
  const options = [requirement, ...(requirement.alternatives ?? [])];
  for (const option of options) {
    const doc = byType.get(option.type);
    if (doc) return { doc, usedType: option.type };
  }
  return { usedType: requirement.type };
}

const REQUIREMENTS: Record<EntityType, Requirement[]> = {
  PROPRIETORSHIP: [
    { type: 'AADHAAR_CARD', label: 'Aadhaar Card', required: true, hint: 'Front and back, of the proprietor' },
    { type: 'PAN_CARD', label: 'PAN Card', required: true, hint: 'Individual PAN of the proprietor' },
    { type: 'GST_CERTIFICATE', label: 'GST Registration', required: false, hint: 'If applicable to your turnover' },
    { type: 'UDYAM_CERTIFICATE', label: 'Udyam (MSME) Registration', required: true },
    {
      type: 'CANCELLED_CHEQUE',
      label: 'Bank account proof',
      required: true,
      hint: 'A cancelled cheque in the account name.',
      alternatives: [
        {
          type: 'BANK_STATEMENT',
          label: 'Bank statement (last 3 months)',
          hint: 'PDF from your bank showing the account number, IFSC and holder name.',
        },
      ],
    },
  ],
  PARTNERSHIP: [
    { type: 'PARTNER_AADHAAR', label: 'Partner Aadhaar Cards', required: true, hint: 'One per partner on the deed' },
    { type: 'PARTNER_PAN', label: 'Partner PAN Cards', required: true, hint: 'One per partner on the deed' },
    { type: 'FIRM_PAN_CARD', label: 'Firm PAN Card', required: true },
    { type: 'GST_CERTIFICATE', label: 'GST Registration', required: false },
    { type: 'UDYAM_CERTIFICATE', label: 'Udyam (MSME) Registration', required: true },
    { type: 'PARTNERSHIP_DEED', label: 'Partnership Deed', required: true },
    {
      type: 'CANCELLED_CHEQUE',
      label: 'Bank account proof',
      required: true,
      hint: 'A cancelled cheque in the account name.',
      alternatives: [
        {
          type: 'BANK_STATEMENT',
          label: 'Bank statement (last 3 months)',
          hint: 'PDF from your bank showing the account number, IFSC and holder name.',
        },
      ],
    },
    { type: 'AUTHORIZATION_RESOLUTION', label: 'Authorization Resolution', required: true, hint: 'Naming the authorised signatory' },
  ],
  LLP: [
    { type: 'CERTIFICATE_OF_INCORPORATION', label: 'Certificate of Incorporation', required: true },
    { type: 'PARTNER_AADHAAR', label: 'Designated Partner Aadhaar', required: true },
    { type: 'PARTNER_PAN', label: 'Designated Partner PAN', required: true },
    { type: 'FIRM_PAN_CARD', label: 'LLP PAN Card', required: true },
    { type: 'GST_CERTIFICATE', label: 'GST Registration', required: false },
    { type: 'PARTNERSHIP_DEED', label: 'LLP Agreement', required: true },
    {
      type: 'CANCELLED_CHEQUE',
      label: 'Bank account proof',
      required: true,
      hint: 'A cancelled cheque in the account name.',
      alternatives: [
        {
          type: 'BANK_STATEMENT',
          label: 'Bank statement (last 3 months)',
          hint: 'PDF from your bank showing the account number, IFSC and holder name.',
        },
      ],
    },
  ],
  PRIVATE_LIMITED: [
    { type: 'CERTIFICATE_OF_INCORPORATION', label: 'Certificate of Incorporation', required: true },
    { type: 'GST_CERTIFICATE', label: 'GST Registration', required: false },
    { type: 'MOA', label: 'Memorandum of Association', required: true },
    { type: 'AOA', label: 'Articles of Association', required: true },
    { type: 'COMPANY_PAN_CARD', label: 'Company PAN Card', required: true },
    { type: 'DIRECTOR_AADHAAR', label: 'Director Aadhaar Cards', required: true, hint: 'One per director on record' },
    { type: 'DIRECTOR_PAN', label: 'Director PAN Cards', required: true, hint: 'One per director on record' },
    { type: 'UDYAM_CERTIFICATE', label: 'Udyam (MSME) Registration', required: false },
    {
      type: 'CANCELLED_CHEQUE',
      label: 'Bank account proof',
      required: true,
      hint: 'A cancelled cheque in the account name.',
      alternatives: [
        {
          type: 'BANK_STATEMENT',
          label: 'Bank statement (last 3 months)',
          hint: 'PDF from your bank showing the account number, IFSC and holder name.',
        },
      ],
    },
    { type: 'BOARD_RESOLUTION', label: 'Board Resolution', required: true },
  ],
  PUBLIC_LIMITED: [
    { type: 'CERTIFICATE_OF_INCORPORATION', label: 'Certificate of Incorporation', required: true },
    { type: 'MOA', label: 'Memorandum of Association', required: true },
    { type: 'AOA', label: 'Articles of Association', required: true },
    { type: 'COMPANY_PAN_CARD', label: 'Company PAN Card', required: true },
    { type: 'DIRECTOR_AADHAAR', label: 'Director Aadhaar Cards', required: true },
    { type: 'DIRECTOR_PAN', label: 'Director PAN Cards', required: true },
    { type: 'GST_CERTIFICATE', label: 'GST Registration', required: true },
    {
      type: 'CANCELLED_CHEQUE',
      label: 'Bank account proof',
      required: true,
      hint: 'A cancelled cheque in the account name.',
      alternatives: [
        {
          type: 'BANK_STATEMENT',
          label: 'Bank statement (last 3 months)',
          hint: 'PDF from your bank showing the account number, IFSC and holder name.',
        },
      ],
    },
    { type: 'BOARD_RESOLUTION', label: 'Board Resolution', required: true },
  ],
};

const ACCEPTED = ['application/pdf', 'image/jpeg', 'image/png'];
const MAX_BYTES = 10 * 1024 * 1024;

interface KycResponse {
  merchant: {
    merchant_ref: string;
    legal_name: string;
    entity_type: EntityType;
    kyc_status: string;
    kyc_submitted_at: string | null;
    kyc_rejection_reason: string | null;
    is_live: number;
  };
  documents: MerchantDocument[];
}

function DocumentRow({
  requirement,
  document,
  onUpload,
  uploading,
}: {
  requirement: Requirement;
  document?: MerchantDocument;
  onUpload: (file: File, type: DocumentType) => void;
  uploading: boolean;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [localError, setLocalError] = useState<string | null>(null);
  // Which of the accepted document types the next file will be filed as.
  const [choice, setChoice] = useState<DocumentType>(requirement.type);

  const options = [
    { type: requirement.type, label: requirement.alternatives ? 'Cancelled cheque' : requirement.label, hint: requirement.hint },
    ...(requirement.alternatives ?? []),
  ];
  const active = options.find((o) => o.type === choice) ?? options[0]!;

  function handleFile(file: File | undefined) {
    if (!file) return;
    // Validate before the request so a bad file costs no round trip.
    if (!ACCEPTED.includes(file.type)) {
      setLocalError('Only PDF, JPG and PNG files are accepted.');
      return;
    }
    if (file.size > MAX_BYTES) {
      setLocalError('Files must be 10 MB or smaller.');
      return;
    }
    setLocalError(null);
    onUpload(file, choice);
  }

  const needsAttention =
    document?.verification_status === 'REJECTED' || document?.verification_status === 'REUPLOAD_REQUESTED';

  return (
    <li className={`px-4 py-4 ${needsAttention ? 'bg-warning-soft' : ''}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex min-w-0 items-start gap-3">
          <span
            className={`mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-md border ${
              document?.verification_status === 'VERIFIED'
                ? 'border-brand-800 bg-success-soft'
                : 'border-line bg-surface-inset'
            }`}
          >
            {document?.verification_status === 'VERIFIED' ? (
              <CheckCircle2 className="h-4 w-4 text-brand-400" />
            ) : (
              <FileText className="h-4 w-4 text-content-subtle" />
            )}
          </span>
          <div className="min-w-0">
            <p className="flex flex-wrap items-center gap-2 text-sm">
              {requirement.label}
              {!requirement.required && (
                <span className="text-2xs uppercase tracking-wider text-content-faint">Conditional</span>
              )}
              {document && <StatusBadge status={document.verification_status} />}
            </p>
            {!document && active.hint && (
              <p className="mt-1 text-xs text-content-subtle">{active.hint}</p>
            )}

            {/* Either/or requirements: a segmented picker rather than two
                separate rows, because only one of them is ever needed and
                two rows reads as twice the work. */}
            {!document && requirement.alternatives && (
              <div
                role="radiogroup"
                aria-label={`${requirement.label} — choose one`}
                className="mt-2.5 inline-flex rounded-md border border-line bg-surface-inset p-0.5"
              >
                {options.map((option) => (
                  <button
                    key={option.type}
                    type="button"
                    role="radio"
                    aria-checked={choice === option.type}
                    onClick={() => setChoice(option.type)}
                    className={`rounded px-2.5 py-1 text-xs transition-colors ${
                      choice === option.type
                        ? 'bg-surface-overlay text-content'
                        : 'text-content-subtle hover:text-content-muted'
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            )}
            {document && (
              <p className="mt-1 text-2xs text-content-faint">
                {document.file_name} · v{document.version} · uploaded {relativeTime(document.uploaded_at)}
              </p>
            )}
            {document?.rejection_comment && (
              <p className="mt-2 flex items-start gap-1.5 text-xs text-[#FCD34D]">
                <AlertCircle className="mt-0.5 h-3 w-3 shrink-0" />
                {document.rejection_comment}
              </p>
            )}
            {localError && <p className="mt-2 text-xs text-danger">{localError}</p>}
          </div>
        </div>

        <div className="shrink-0">
          <input
            ref={inputRef}
            type="file"
            accept={ACCEPTED.join(',')}
            className="sr-only"
            onChange={(e) => handleFile(e.target.files?.[0])}
          />
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            className={`btn ${needsAttention ? 'btn-primary' : 'btn-secondary'} h-8 text-xs`}
            disabled={uploading}
          >
            {uploading ? <Spinner className="h-3.5 w-3.5" /> : <Upload className="h-3.5 w-3.5" />}
            {document ? 'Replace' : 'Upload'}
          </button>
        </div>
      </div>
    </li>
  );
}

export function Onboarding() {
  const queryClient = useQueryClient();
  const session = useAuth((s) => s.session);
  const [uploadingType, setUploadingType] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const { data, isLoading } = useQuery({ queryKey: ['kyc'], queryFn: () => api.get<KycResponse>('/kyc') });

  const upload = useMutation({
    mutationFn: async ({ file, type }: { file: File; type: DocumentType }) => {
      const form = new FormData();
      form.append('file', file);
      form.append('document_type', type);

      // Multipart bypasses the shared JSON client — FormData must set its own
      // boundary, so we cannot hand it a Content-Type header.
      const response = await fetch('/api/v1/merchant/kyc/documents', {
        method: 'POST',
        headers: { Authorization: `Bearer ${session?.accessToken ?? ''}` },
        body: form,
      });
      if (!response.ok) {
        const body = await response.json().catch(() => null);
        throw new Error(body?.error?.message ?? 'Upload failed.');
      }
      return response.json();
    },
    onMutate: ({ type }) => setUploadingType(type),
    onSettled: () => setUploadingType(null),
    onSuccess: () => {
      setError(null);
      void queryClient.invalidateQueries({ queryKey: ['kyc'] });
    },
    onError: (err) => setError((err as Error).message),
  });

  const submit = useMutation({
    mutationFn: () => api.post('/kyc/submit'),
    onSuccess: () => {
      setError(null);
      void queryClient.invalidateQueries({ queryKey: ['kyc'] });
    },
    onError: (err) => setError(err instanceof ApiClientError ? err.message : 'Submission failed.'),
  });

  if (isLoading || !data) {
    return (
      <div className="flex items-center gap-2 py-20 text-sm text-content-muted">
        <Spinner /> Loading onboarding status
      </div>
    );
  }

  const requirements = REQUIREMENTS[data.merchant.entity_type] ?? REQUIREMENTS.PRIVATE_LIMITED;
  const byType = new Map(data.documents.map((doc) => [doc.document_type, doc]));
  const missing = requirements.filter((r) => r.required && !satisfiedBy(r, byType).doc);
  const canSubmit = missing.length === 0 && ['DRAFT', 'ACTION_REQUIRED', 'REJECTED'].includes(data.merchant.kyc_status);

  return (
    <div className="max-w-4xl space-y-6">
      {/* ---- Status header ---------------------------------------------- */}
      <div className="panel p-5">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <p className="text-2xs uppercase tracking-wider text-content-subtle">{data.merchant.merchant_ref}</p>
            <h2 className="mt-1 text-lg font-semibold tracking-tight">{data.merchant.legal_name}</h2>
            <p className="mt-1 text-xs text-content-muted">
              {data.merchant.entity_type.replace(/_/g, ' ').toLowerCase()}
              {data.merchant.kyc_submitted_at && ` · submitted ${relativeTime(data.merchant.kyc_submitted_at)}`}
            </p>
          </div>
          <StatusBadge status={data.merchant.kyc_status} />
        </div>

        {data.merchant.kyc_rejection_reason && (
          <p className="mt-4 flex items-start gap-2 rounded border border-danger-ring bg-danger-soft px-3 py-2.5 text-xs text-[#FDA4AF]">
            <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            {data.merchant.kyc_rejection_reason}
          </p>
        )}

        {data.merchant.kyc_status === 'APPROVED' && (
          <p className="mt-4 flex items-start gap-2 rounded border border-brand-800 bg-success-soft px-3 py-2.5 text-xs text-brand-300">
            <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            Approved and live. Generate a live API key from Developer Settings to start processing real payments.
          </p>
        )}
      </div>

      {/* ---- Documents --------------------------------------------------- */}
      <section className="panel overflow-hidden">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-line px-4 py-3">
          <div>
            <h2 className="text-sm font-medium">Required documents</h2>
            <p className="mt-0.5 text-2xs text-content-subtle">PDF, JPG or PNG · up to 10 MB each</p>
          </div>
          <span className="badge badge-neutral">
            {requirements.filter((r) => satisfiedBy(r, byType).doc).length} of {requirements.length} uploaded
          </span>
        </div>

        <ul className="divide-y divide-line-subtle">
          {requirements.map((requirement) => (
            <DocumentRow
              key={requirement.type}
              requirement={requirement}
              document={satisfiedBy(requirement, byType).doc}
              uploading={uploadingType === requirement.type}
              onUpload={(file, type) => upload.mutate({ file, type })}
            />
          ))}
        </ul>
      </section>

      {error && (
        <p role="alert" className="rounded border border-danger-ring bg-danger-soft px-3 py-2.5 text-xs text-[#FDA4AF]">
          {error}
        </p>
      )}

      {/* ---- Submit ------------------------------------------------------ */}
      {data.merchant.kyc_status !== 'APPROVED' && (
        <div className="panel flex flex-wrap items-center justify-between gap-4 p-4">
          <p className="text-xs text-content-muted">
            {missing.length > 0
              ? `${missing.length} required document${missing.length === 1 ? '' : 's'} still to upload: ${missing.map((m) => m.label).join(', ')}.`
              : data.merchant.kyc_status === 'PENDING_REVIEW'
                ? 'Under compliance review. We typically respond within 24–72 business hours.'
                : 'Everything required is uploaded. Submit when you are ready.'}
          </p>
          <button type="button" onClick={() => submit.mutate()} className="btn btn-primary" disabled={!canSubmit || submit.isPending}>
            {submit.isPending ? <Spinner /> : <Send className="h-4 w-4" />}
            Submit for review
          </button>
        </div>
      )}
    </div>
  );
}
